import React, { useState } from "react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function LicenseCell({ player, isAdmin, onChanged }) {
  const [open, setOpen] = useState(false);
  const verified = !!player.license_verified;

  const toggleVerify = async (verify) => {
    try {
      await api.post(`/players/${player.id}/license`, { verified: verify });
      toast.success(verify ? "Lisens verifisert" : "Lisens-verifikasjon trukket tilbake");
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Feil");
    }
  };

  const dotColor = verified ? "bg-emerald-500" : "bg-rose-500";
  const tooltipText = verified
    ? `Lisens bekreftet ${player.license_verified_at ? "(" + new Date(player.license_verified_at).toLocaleDateString("nb-NO") + ")" : ""}`
    : "Lisens ikke bekreftet — kan ikke godkjennes";

  return (
    <>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              data-testid={`license-perle-${player.id}`}
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 hover:opacity-80 cursor-pointer"
            >
              <span className={`block w-3 h-3 rounded-full ${dotColor} ring-2 ring-white shadow`} />
              <span className="text-xs font-heading uppercase tracking-widest text-neutral-700">{verified ? "OK" : "Ikke OK"}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>{tooltipText}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="license-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-2xl">Lisens-status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Spiller</div>
                <div className="font-semibold">{player.full_name}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Fødselsdato</div>
                <div className="font-mono">{player.date_of_birth || "—"}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Lisensnummer</div>
                <div className="font-mono">{player.nihf_license}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Klubb hos NIHF</div>
                <div className="font-semibold">{player.nihf_club || <span className="text-neutral-400">Ikke oppgitt</span>}</div>
              </div>
            </div>

            <div className={`p-4 border-2 rounded-sm ${verified ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`}>
              <div className="flex items-start gap-3">
                <span className={`block w-4 h-4 rounded-full mt-1 ${dotColor}`} />
                <div className="text-sm">
                  {verified ? (
                    <>
                      <div className="font-heading uppercase tracking-widest text-emerald-800 mb-1">Bekreftet</div>
                      <div className="text-emerald-800">Lisens er bekreftet sjekket i SportsAdmin. Lagt inn av {player.registered_by === player.license_verified_by ? "lagleder ved påmelding" : "admin"}.</div>
                    </>
                  ) : (
                    <>
                      <div className="font-heading uppercase tracking-widest text-rose-800 mb-1">Ikke bekreftet</div>
                      <div className="text-rose-800">Spilleren kan ikke godkjennes før lisensen er bekreftet i SportsAdmin.</div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-neutral-200 pt-3">
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">SportsAdmin oppslag</div>
              <a
                href="https://sa.nif.no/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:underline text-sm"
                data-testid="sa-link"
              >
                <ExternalLink className="w-4 h-4 mr-1" />Åpne NIHF SportsAdmin
              </a>
              <p className="text-xs text-neutral-500 mt-2">Søk etter spilleren i SportsAdmin og bekreft rekreasjonshockey-lisens for klubben &quot;{player.nihf_club || "lagets klubb"}&quot;.</p>
            </div>

            {!isAdmin && !verified && (
              <div className="p-3 border border-amber-300 bg-amber-50 rounded-sm text-xs flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                <span>Kun admin kan revertere lisens-status. Kontakt serieledelsen om noe er feil.</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">Lukk</Button>
            {isAdmin && (verified ? (
              <Button data-testid="revoke-license-btn" onClick={() => toggleVerify(false)} className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm font-heading uppercase tracking-widest">Fjern bekreftelse</Button>
            ) : (
              <Button data-testid="verify-license-btn" onClick={() => toggleVerify(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-heading uppercase tracking-widest">Bekreft lisens</Button>
            ))}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
